import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Static privacy policy page for 目异同学 app and coca.xinyixue.art.
 * Accessible at /privacy without login. Content in Chinese per best practice.
 * Ref: app privacy policy essentials (data collected, purpose, storage, sharing, user rights).
 */
const PRIVACY_LAST_UPDATED = '2026年3月';

export const PrivacyPage: React.FC = () => {
  useEffect(() => {
    document.title = '隐私政策 - 目异同学';
    return () => {
      document.title = '目异同学';
    };
  }, []);

  return (
    <div className="h-screen min-h-screen bg-[#F4F1EC] text-[#333] overflow-y-auto overflow-x-hidden flex flex-col">
      <header className="sticky top-0 z-10 border-b border-[#e5e2dd] bg-[#F4F1EC]/95 backdrop-blur py-3 px-4 shrink-0">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="text-[#666] hover:text-[#333] text-sm">
            ← 返回
          </Link>
          <h1 className="text-lg font-semibold">隐私政策</h1>
          <span className="w-10" aria-hidden />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 pb-24 flex-1 min-h-0">
        <article className="space-y-8">
          <div className="text-sm text-[#666]">
            <p><strong>生效日期：</strong>自本应用首次发布之日起生效</p>
            <p><strong>更新日期：</strong>{PRIVACY_LAST_UPDATED}</p>
            <p><strong>运营者：</strong>新艺学目异(北京)教育科技有限公司</p>
          </div>

          <p className="leading-relaxed">
            目异同学（以下简称「本应用」）是由 新艺学目异(北京)教育科技有限公司（以下简称「我们」）提供的、面向平板设备的互动学习应用。我们尊重并保护用户隐私，请在使用本应用前仔细阅读本政策。
          </p>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold border-b border-[#ddd] pb-2">一、我们如何收集与使用信息</h2>

            <h3 className="text-base font-medium mt-6">1.1 本应用（客户端）涉及的功能与权限</h3>
            <p className="leading-relaxed">
              本应用为平板端学习工具，主要提供横屏学习界面、拍照与相册上传学习资料/作业等功能。我们仅在实现相关功能所必需的范围内申请以下权限：
            </p>
            <div className="overflow-x-auto my-4">
              <table className="w-full border-collapse border border-[#ddd] text-sm">
                <thead>
                  <tr className="bg-[#f8f6f3]">
                    <th className="border border-[#ddd] px-3 py-2 text-left">权限名称</th>
                    <th className="border border-[#ddd] px-3 py-2 text-left">用途说明</th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td className="border border-[#ddd] px-3 py-2">网络访问（INTERNET）</td><td className="border border-[#ddd] px-3 py-2">加载在线学习内容、同步学习数据、提交作业等，需连接学习服务平台。</td></tr>
                  <tr><td className="border border-[#ddd] px-3 py-2">网络状态（ACCESS_NETWORK_STATE / ACCESS_WIFI_STATE）</td><td className="border border-[#ddd] px-3 py-2">检测网络连接状态，在无网络或网络异常时提示用户，优化加载体验。</td></tr>
                  <tr><td className="border border-[#ddd] px-3 py-2">相机（CAMERA）</td><td className="border border-[#ddd] px-3 py-2">用于拍摄学习资料、作业等照片并上传至学习平台。</td></tr>
                  <tr><td className="border border-[#ddd] px-3 py-2">存储/相册（READ_EXTERNAL_STORAGE、WRITE_EXTERNAL_STORAGE、READ_MEDIA_IMAGES）</td><td className="border border-[#ddd] px-3 py-2">用于选择相册中已有图片上传为作业或学习资料；在您同意的情况下，可将拍摄的照片保存至相册。</td></tr>
                </tbody>
              </table>
            </div>
            <p className="leading-relaxed">上述权限均用于实现产品功能，不会在未经您同意的情况下用于与学习服务无关的目的。</p>

            <h3 className="text-base font-medium mt-6">1.2 学习内容与账号数据</h3>
            <p className="leading-relaxed">
              本应用内嵌的学习内容、登录账号、作业与学习数据等，由我们合作的<strong>学习服务平台</strong>（域名为 coca.xinyixue.art）提供与处理。您在该平台上的注册信息、学习记录、上传的图片等，其收集、存储与使用规则以该平台的用户协议与隐私政策为准。本应用仅作为访问该平台的客户端，不另行存储您的账号密码或学习内容于本机之外。
            </p>

            <h3 className="text-base font-medium mt-6">1.3 我们可能收集的信息类型</h3>
            <ul className="list-disc pl-6 space-y-2 leading-relaxed">
              <li><strong>设备与日志信息：</strong>为保障服务稳定与排查问题，我们可能收集设备型号、系统版本、应用版本及必要的运行日志（不包含您的账号密码或学习内容）。</li>
              <li><strong>您主动提供的信息：</strong>例如通过客服或反馈渠道向我们提供的联系方式、反馈内容等。</li>
            </ul>
            <p className="leading-relaxed mt-2">我们不会在未经法律允许或您同意的情况下，将您的个人信息提供给无关第三方用于营销或其它与学习服务无关的目的。</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold border-b border-[#ddd] pb-2">二、信息的存储与保护</h2>
            <ul className="list-disc pl-6 space-y-2 leading-relaxed">
              <li>本应用产生的本地数据（如缓存）仅存储在您的设备上，您可通过卸载应用清除。</li>
              <li>学习相关数据由学习服务平台在其服务器上存储，具体保留期限与安全措施以该平台政策为准。</li>
              <li>我们采取合理的技术与管理措施，降低信息泄露、损毁或丢失的风险。</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold border-b border-[#ddd] pb-2">三、信息的共享、转让与公开</h2>
            <p className="leading-relaxed">我们不会与第三方共享您的个人信息，除非：</p>
            <ul className="list-disc pl-6 space-y-2 leading-relaxed">
              <li>事先获得您的明确同意；</li>
              <li>根据法律法规或行政机关、司法机关依法要求；</li>
              <li>与我们的关联方或授权服务商共享，且仅限为实现本政策所述目的所必需，并受合同约束。</li>
            </ul>
            <p className="leading-relaxed">我们不会将您的个人信息转让给任何公司、组织或个人，但以下情况除外：事先获得您的明确同意；或根据法律法规或强制性的行政、司法要求。</p>
            <p className="leading-relaxed">我们不会公开披露您的个人信息，除非获得您的单独同意或法律另有规定。</p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold border-b border-[#ddd] pb-2">四、您的权利</h2>
            <p className="leading-relaxed">您有权：</p>
            <ul className="list-disc pl-6 space-y-2 leading-relaxed">
              <li><strong>查询、更正个人信息：</strong>如学习服务平台或本应用提供账号或个人信息管理入口，您可通过该入口查询、更正您的信息。</li>
              <li><strong>删除与注销：</strong>您可联系学习服务平台或我们，申请删除相关个人信息或注销账号，我们将在法律规定的期限内处理。</li>
              <li><strong>撤回同意：</strong>您可通过关闭相关权限（如相机、存储）或停止使用本应用撤回部分授权；撤回后我们可能无法继续提供对应功能。</li>
              <li><strong>投诉与举报：</strong>如您认为我们处理您的个人信息时违反法律或约定，您可向我们或相关监管部门投诉、举报。</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold border-b border-[#ddd] pb-2">五、未成年人保护</h2>
            <p className="leading-relaxed">
              如您为未成年人，请在监护人同意的前提下使用本应用。我们不会主动收集未成年人的个人信息；若您是未成年人的监护人并对我们收集的未成年人信息有疑问，请通过下文联系方式与我们联系，我们将尽快核实并处理。
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold border-b border-[#ddd] pb-2">六、本政策的更新与适用</h2>
            <p className="leading-relaxed">
              我们可能适时修订本政策，修订后的政策将在应用内或通过其他适当方式公布；若涉及重大变更，我们会通过应用内通知等方式提示您。继续使用本应用即表示您接受更新后的政策。
            </p>
            <p className="leading-relaxed">
              本政策适用于目异同学应用（包括 Android 及各渠道版本）；学习服务平台（coca.xinyixue.art）的数据处理以其自身政策为准。
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold border-b border-[#ddd] pb-2">七、联系我们</h2>
            <p className="leading-relaxed">如您对本政策或个人信息处理有任何疑问、意见或建议，请通过以下方式联系我们：</p>
            <ul className="list-none space-y-1 leading-relaxed text-[#555]">
              <li><strong>运营者：</strong>新艺学目异(北京)教育科技有限公司</li>
              <li><strong>联系邮箱：</strong>mikuzzz@outlook.com</li>
              <li><strong>联系地址：</strong>北京市朝阳区南湖西园227号楼2301</li>
            </ul>
            <p className="leading-relaxed">我们将在收到您的反馈后尽快回复。</p>
          </section>

          <p className="text-[#666] text-sm leading-relaxed border-t border-[#ddd] pt-6 mt-8">
            <strong>再次提醒：</strong>请您在使用本应用前仔细阅读并理解本政策。若您不同意本政策，请勿使用本应用。
          </p>
        </article>
      </main>
    </div>
  );
};

export default PrivacyPage;
